import type { ReactNode } from 'react';
import { Button, Space, Tooltip, Typography } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import {
  ACTION_GROUP_LABEL_STYLE,
  blockButtonStyle,
} from '@/components/shared/actionMenuStyle';
import type { OrderPanelEntry, OrderPanelSections } from './orderPanelActions';

/** Cùng bề rộng panel hàng loạt bên Ecom (`BatchActionPanel`). */
const PANEL_WIDTH = 230;
/**
 * TCR-2504 — panel nay chứa mọi hành động của menu ⋮ cũ (tới ~13 nút, cao ~550px) nên màn thấp
 * (laptop 1366×768, điện thoại) sẽ tràn đáy. Danh sách nút cuộn bên trong; 320px ≈ từ đỉnh trang
 * tới dưới tiêu đề bảng (~250px) + tiêu đề và padding của panel (~50px) + chừa mép dưới. Đo bằng
 * `test/e2e/tcr-2504.spec.ts` ở 375×812, 768×1024, 1366×768, 1920×1080 (280px tràn 9px ở 1366×768).
 */
const LIST_MAX_HEIGHT = 'max(200px, calc(100dvh - 320px))';

interface PosBatchActionPanelProps {
  selectedCount: number;
  sections: OrderPanelSections;
  busy: boolean;
  onAction: (key: string) => void;
  onClear: () => void;
}

/** Tiêu đề nhóm chức năng: in đậm màu đen, giống panel Ecom. */
function SectionLabel({ children }: { children: ReactNode }) {
  return <Typography.Text style={ACTION_GROUP_LABEL_STYLE}>{children}</Typography.Text>;
}

function ActionButton({ item, busy, onAction }: { item: OrderPanelEntry; busy: boolean; onAction: (key: string) => void }) {
  const disabled = busy || item.disabled;
  const button = (
    <Button
      block
      size="small"
      icon={item.icon}
      disabled={disabled}
      // Nút disabled theo chuẩn toàn app (`ThemeProvider`: nền #f5f5f5, chữ #6b6b6b, `!important`) —
      // KHÔNG chồng thêm opacity, chữ xám mờ thêm nữa là không đọc được (TCR-2504, ảnh 1366×768).
      style={blockButtonStyle(item.tone)}
      onClick={() => onAction(item.key)}
    >
      {item.count === undefined ? item.text : `${item.text} (${item.count})`}
    </Button>
  );
  // Nút disabled chặn pointer-events — bọc span để tooltip lý do vẫn hiện khi hover.
  return item.tooltip
    ? <Tooltip title={item.tooltip}><span style={{ display: 'block' }}>{button}</span></Tooltip>
    : button;
}

/**
 * TCR-2264/2504 — menu hành động duy nhất của trang Đơn hàng, cùng bố cục/màu với `BatchActionPanel`
 * của Ecom: nhóm "Chuyển trạng thái" và "Hành động", nút khối màu theo tông. Nút nào hiện và đếm bao
 * nhiêu đơn do `orderPanelActions.tsx` quyết; side-effect nằm ở `Orders/index.tsx`.
 */
export function PosBatchActionPanel({ selectedCount, sections, busy, onAction, onClear }: PosBatchActionPanelProps) {
  return (
    <div style={{ width: PANEL_WIDTH }}>
      <Typography.Text strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
        {`Hành động cho ${selectedCount} đơn hàng`}
      </Typography.Text>

      <div data-testid="order-action-panel-list" style={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto', overscrollBehavior: 'contain' }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {sections.transition.length > 0 ? <SectionLabel>Chuyển trạng thái</SectionLabel> : null}
          {sections.transition.map((item) => <ActionButton key={item.key} item={item} busy={busy} onAction={onAction} />)}

          <SectionLabel>Hành động</SectionLabel>
          {sections.action.map((item) => <ActionButton key={item.key} item={item} busy={busy} onAction={onAction} />)}

          <Button block size="small" style={blockButtonStyle()} icon={<CloseCircleOutlined />} onClick={onClear}>
            Bỏ chọn
          </Button>
        </Space>
      </div>
    </div>
  );
}
